import { can, ROLES, type Capability, type Role } from '@sauda/shared';
import {
  iconBell,
  iconHandshake,
  iconHandshakeFill,
  iconHome,
  iconHomeFill,
  iconLocation,
  iconPerson,
  iconPayments,
  iconPaymentsFill,
  iconReceipt,
  iconRenew,
  iconBrand,
  iconPartners,
  iconSettings,
  iconToday,
  iconTodayFill,
  iconVisit,
  iconVisitFill,
  iconWallet,
  type IconData,
} from '@sauda/ui/kit';

export interface Destination {
  key: string;
  /** i18n key of the label. */
  label: string;
  path: string;
  /** The capability that must be held to reach it. */
  needs: Capability | null;
  icon: IconData;
  activeIcon?: IconData;
}

const D = {
  today: {
    key: 'today',
    label: 'nav.today',
    path: '/',
    needs: null,
    icon: iconToday,
    activeIcon: iconTodayFill,
  },
  money: {
    key: 'money',
    label: 'nav.money',
    path: '/',
    needs: 'brokerage.record',
    icon: iconPayments,
    activeIcon: iconPaymentsFill,
  },
  visits: {
    key: 'visits',
    label: 'nav.visits',
    path: '/visits',
    needs: 'visits.read',
    icon: iconVisit,
    activeIcon: iconVisitFill,
  },
  deals: {
    key: 'deals',
    label: 'nav.deals',
    path: '/deals',
    needs: 'deals.run',
    icon: iconHandshake,
    activeIcon: iconHandshakeFill,
  },
  properties: {
    key: 'properties',
    label: 'nav.properties',
    path: '/properties',
    needs: 'properties.read',
    icon: iconHome,
    activeIcon: iconHomeFill,
  },
  renewals: {
    key: 'renewals',
    label: 'nav.renewals',
    path: '/renewals',
    needs: 'renewals.read',
    icon: iconRenew,
  },
  passbook: {
    key: 'passbook',
    label: 'nav.passbook',
    path: '/passbook',
    needs: 'passbook.read',
    icon: iconWallet,
  },
  billing: {
    key: 'billing',
    label: 'nav.billing',
    path: '/billing',
    needs: 'billing.view',
    icon: iconReceipt,
  },
  notifications: {
    key: 'notifications',
    label: 'nav.notifications',
    path: '/notifications',
    needs: 'notifications.own',
    icon: iconBell,
  },
  team: {
    key: 'team',
    label: 'nav.team',
    path: '/team',
    needs: 'members.manage',
    icon: iconPerson,
  },
  branches: {
    key: 'branches',
    label: 'nav.branches',
    path: '/branches',
    needs: 'branches.view',
    icon: iconLocation,
  },
  brand: {
    key: 'brand',
    label: 'nav.brand',
    path: '/brand',
    needs: 'brand.manage',
    icon: iconBrand,
  },
  partners: {
    key: 'partners',
    label: 'nav.partners',
    path: '/partners',
    needs: 'partners.manage',
    icon: iconPartners,
  },
  settings: {
    key: 'settings',
    label: 'nav.settings',
    path: '/settings',
    needs: null,
    icon: iconSettings,
  },
} satisfies Record<string, Destination>;

export const DESTINATIONS: Destination[] = Object.values(D);
export const MAX_TABS = 5;

/** Account level pages gathered under Settings, in the order the page lists them. */
export function settingsPagesFor(role: Role): Destination[] {
  return [D.brand, D.team, D.branches, D.partners, D.billing].filter(
    (d) => d.needs === null || can(role, d.needs),
  );
}

export interface Navigation {
  /** The bottom bar or rail: at most five, the first is the role's home. */
  tabs: Destination[];
  /** Behind a More tab, when there is room for one. */
  more: Destination[];
  /** Everything else a role can reach, listed in the account sheet. */
  extras: Destination[];
}

/**
 * Where each role goes (design flows 0.2). Tabs hold the daily work; what is left sits behind
 * More, or in the account sheet for a role whose five tabs are all daily work. Nothing a role
 * may open is left without a way to reach it.
 */
export function navigationFor(role: Role): Navigation {
  const has = (d: Destination): boolean => d.needs === null || can(role, d.needs);
  const keep = (list: Destination[]) => list.filter(has);
  if (role === 'accounts') {
    return {
      tabs: keep([
        D.money,
        D.passbook,
        { ...D.billing, label: 'nav.invoices' },
        { ...D.notifications, label: 'nav.alerts' },
      ]),
      more: [],
      extras: keep([D.properties, D.branches]),
    };
  }
  if (role === 'agent') {
    return {
      tabs: keep([D.today, D.visits, D.deals, D.properties, D.renewals]),
      more: [],
      extras: keep([D.passbook, D.branches, D.notifications]),
    };
  }
  return {
    tabs: keep([D.today, D.visits, D.deals, D.properties]),
    more: keep([
      D.renewals,
      D.passbook,
      ...(settingsPagesFor(role).length > 0 ? [D.settings] : []),
    ]),
    extras: keep([D.notifications]),
  };
}

/** Pages every role may open that are not a destination of their own: the guide, from the account sheet. */
const SHARED_PAGES = ['/help'];
const isShared = (path: string): boolean =>
  SHARED_PAGES.some((p) => path === p || path.startsWith(`${p}/`));

/** True when a role may open the destination (used to refuse a link typed by hand). */
export function reachable(role: Role, path: string): boolean {
  if (isShared(path)) return true;
  const { tabs, more, extras } = navigationFor(role);
  if (
    more.some((d) => d.key === 'settings') &&
    settingsPagesFor(role).some((d) => path === d.path || path.startsWith(`${d.path}/`))
  )
    return true;
  return [...tabs, ...more, ...extras].some((d) => d.path === path);
}

const SETTINGS_PATHS = [D.brand, D.team, D.branches, D.partners, D.billing].map((d) => d.path);

/** The tab to highlight for a path: the longest match, More for a page behind it, none for a shared page. */
export function activeKey(path: string, tabs: Destination[]): string {
  if (isShared(path)) return '';
  if (
    tabs.some((d) => d.key === 'settings') &&
    SETTINGS_PATHS.some((p) => path === p || path.startsWith(`${p}/`))
  )
    return 'settings';
  const hit = tabs
    .filter((d) => d.path !== '/' && (path === d.path || path.startsWith(`${d.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (hit !== undefined) return hit.key;
  return path === '/' ? (tabs[0]?.key ?? '') : 'more';
}

export const ALL_ROLES = ROLES;
